import axios from 'axios';
import { flattenGizmoConfigs } from '../../gizmos/utils';
import {
  IFlowyError,
  IGizmo,
  IIndex,
  IIndexColumn,
  IUserV2,
} from '../../types';
import Dao from '../dao';

interface IParams {
  type: 'rootGizmo' | 'rootGizmoData';
  fid?: string;
  page?: number;
  pageSize?: number;
  gizmoConfig?: IGizmo;
  context: {
    apiUrl: string;
    user: IUserV2;
    dao: Dao;
  };
}

/**
 * Get index data from the Flowy servers
 *
 * @param type type of data we're bringing from the server
 * @param {string} fid Flowy id of the root gizmo from which we're retrieving the root gizmo data
 * @param {number} page number of page we're going to retrieve from the server
 * @param {number} pageSize number of elements we're going to bring from the server
 * @param {IGizmo} gizmoConfig: gizmo configuration, it's used to add operations column
 *
 * @returns
 */
function getIndex({
  type,
  fid,
  page,
  pageSize,
  gizmoConfig,
  context,
}: IParams): Promise<IIndex | undefined> {
  return new Promise<IIndex | undefined>(async (resolve, reject) => {
    const { apiUrl, user, dao } = context;

    if (user.credentials) {
      if (type === 'rootGizmo') {
        const credentials: any = user.credentials;
        let url = `${apiUrl}/main_api/v1/root_gizmo/`;
        if (page !== undefined || pageSize !== undefined) {
          url += '?';
          if (page !== undefined) {
            url += `page=${page}`;
          }
          if (pageSize !== undefined) {
            url += `&page-size=${pageSize}`;
          }
        }

        try {
          const formsIndexResponse = await axios.post(
            url,
            {
              projection: {
                title: 1,
                permissions: 1,
              },
            },
            {
              headers: {
                ...credentials,
              },
            }
          );

          if (formsIndexResponse.status === 200) {
            const { forms } = formsIndexResponse.data;
            const total = +formsIndexResponse.headers['x-total'];
            const pageFromServer = +formsIndexResponse.headers['x-page'];
            const pageSizeFromServer = +formsIndexResponse.headers[
              'x-per-page'
            ];

            // TODO: pagination data

            const labelColumn: IIndexColumn = {
              fid: 'c1',
              label: 'Nombre formato',
              path: 'title',
            };

            const index: IIndex = {
              fid: 'fid',
              pagination: {
                page: pageFromServer,
                pageSize: pageSizeFromServer,
                total,
              },
              columns: [labelColumn],
              rows: forms,
            };

            if (
              gizmoConfig?.ops?.index?.operations &&
              Array.isArray(gizmoConfig.ops.index.operations)
            ) {
              const operationsColumn: IIndexColumn = {
                fid: 'c2',
                label: 'Ops',
                type: 'operations',
                operations: [],
              };

              for (const operation of gizmoConfig.ops.index.operations) {
                operationsColumn.operations?.push(operation);
              }
              index.columns.push(operationsColumn);
            }

            return resolve(index);
          }
        } catch (e) {
          console.log('getIndex.rootGizmo*-*-*-*-*-*-*-*-*-*-*-*-*-e: ', e);
        }

        return resolve(undefined);
      } else if (type === 'rootGizmoData' && fid) {
        const credentials: any = user.credentials;

        const columns: IIndexColumn[] = [];
        const forms = (
          await dao.getRootGizmoConfigs({
            fid,
          })
        ).filter(f => f.fid === fid);

        let label = '';
        let createButtonLabel = '';
        let showStatus = gizmoConfig?.ops?.index?.registers?.showStatus;

        for (const form of forms) {
          const flattenedGizmoConfigs = flattenGizmoConfigs({
            gizmos: form.gizmos!,
          });
          const columnGizmos = flattenedGizmoConfigs.filter(
            g => g.ops?.indexColumn?.isDisplayed
          );
          label = form.label;
          createButtonLabel =
            form.ops?.form?.registersIndex?.createButton?.label || '';
          if (
            form.ops?.form?.registersIndex?.statusColumn?.display
              ?.isDisplayed !== undefined
          ) {
            showStatus =
              form.ops?.form?.registersIndex?.statusColumn?.display
                ?.isDisplayed;
          }

          if (columnGizmos) {
            for (const columnGizmo of columnGizmos) {
              const column: IIndexColumn = {
                fid: columnGizmo.fid,
                label: columnGizmo.label,
                gizmo: columnGizmo,
              };
              columns.push(column);
            }
          }
        }

        if (showStatus && forms.length === 1) {
          const statusColumn: IIndexColumn = {
            fid: 'cS',
            label: 'Status',
            type: 'status',
            status: forms[0].status,
          };

          columns.unshift(statusColumn);
        }

        const registersIndexUrl = `${apiUrl}/main_api/v1/root_gizmo/data/${fid}`;
        try {
          const registersIndexResponse = await axios.post(
            registersIndexUrl,
            {
              page,
              pageSize,
            },
            {
              headers: {
                ...credentials,
              },
            }
          );

          if (registersIndexResponse.status === 200) {
            const { filledForms } = registersIndexResponse.data;
            const total = +registersIndexResponse.headers['x-total'];
            const pageFromServer = +registersIndexResponse.headers['x-page'];
            const pageSizeFromServer = +registersIndexResponse.headers[
              'x-per-page'
            ];

            if (filledForms && Array.isArray(filledForms)) {
              const index: IIndex = {
                fid,
                pagination: {
                  page: pageFromServer,
                  pageSize: pageSizeFromServer,
                  total,
                },
                columns,
                rows: filledForms,
              };

              if (label) {
                index.label = label;
              }
              if (createButtonLabel) {
                index.createButton = {
                  label: createButtonLabel,
                };
              }

              if (
                gizmoConfig?.ops?.index?.operations &&
                Array.isArray(gizmoConfig.ops.index.operations)
              ) {
                const operationsColumn: IIndexColumn = {
                  fid: 'c2',
                  label: 'Ops',
                  type: 'operations',
                  operations: [],
                };

                for (const operation of gizmoConfig.ops.index.operations) {
                  operationsColumn.operations?.push(operation);
                }

                index.columns.push(operationsColumn);
              }

              return resolve(index);
            }
          }
        } catch (e) {
          console.log('getIndex.rootGizmoData*-*-*-*-*-*-*-*-*-*-*-*-*-e: ', e);
        }

        resolve(undefined);
      }
    } else {
      const error: IFlowyError = {
        origin: 'dao',
        code: 'user.not-auth',
        message: 'El usuario no está autenticado.',
      };

      reject(error);
    }
  });
}

export default getIndex;
